import {useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {DiagnosisCodesLinkToggle} from '@/features/projects/components/diagnosis-codes-link-toggle/DiagnosisCodesLinkToggle'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {OtherPathologiesFormSchema} from '@/features/projects/formSchemas'
import {DiagnosisCodesPair, useDiagnosisCodesLink} from '@/features/projects/hooks/useDiagnosisCodesLink'
import {useTranslation} from '@/translations/i18n'

interface ProjectOtherPathologiesFieldsProps {
    index: number
    disabledIcd9: string[]
    disabledIcd10: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectOtherPathologiesFields = ({
    index,
    disabledIcd9,
    disabledIcd10,
    showTrashButton,
    remove
}: ProjectOtherPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<OtherPathologiesFormSchema>()
    const {field} = useController({control, name: `otherPathologies.${index}`})

    const handleChangeCodes = useCallback(
        ({icd9, icd10}: DiagnosisCodesPair) => {
            field.onChange({...field.value, diagnosis: icd9, icd10Diagnosis: icd10})
        },
        [field.onChange, field.value]
    )

    // Same rule as the main pathologies: two independent fields kept aligned by the chain.
    const {isLinked, isIcd9Selectable, onToggleLink, handleSelectIcd9, handleSelectIcd10} = useDiagnosisCodesLink({
        icd9: field.value.diagnosis,
        icd10: field.value.icd10Diagnosis,
        onChange: handleChangeCodes
    })

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5} align="flex-end">
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd9_cm', {number: index + 1})}
                    placeholder={t('projects:pathology_number_placeholder')}
                    value={field.value.diagnosis}
                    onChange={handleSelectIcd9}
                    disabledOptions={disabledIcd9}
                    disabled={!isIcd9Selectable}
                    isClearable
                    version="ICD9"
                />
            </div>
            <DiagnosisCodesLinkToggle isLinked={isLinked} isEditable={isIcd9Selectable} onToggle={onToggleLink} />
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd10_im', {number: index + 1})}
                    placeholder={t('projects:pathology_number_icd10_im_placeholder')}
                    value={field.value.icd10Diagnosis}
                    onChange={handleSelectIcd10}
                    disabledOptions={disabledIcd10}
                    isClearable
                    version="ICD10"
                />
            </div>
            {showTrashButton && (
                <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                    <Trash01Icon />
                </Button>
            )}
        </Flexbox>
    )
}
